import { useCallback, useSyncExternalStore } from 'react'

type Listener = () => void

const events = new Map<number, unknown>()
const listeners = new Map<number, Set<Listener>>()

const notify = (id: number): void => {
  const set = listeners.get(id)
  if (!set) return
  for (const listener of set) listener()
}

export const scheduleEventStore = {
  get: (id: number): unknown => events.get(id),
  set: (id: number, data: unknown): void => {
    events.set(id, data)
    notify(id)
  },
  remove: (id: number): void => {
    if (events.delete(id)) notify(id)
  },
  reset: (): void => {
    events.clear()
  },
  hydrate: (items: Array<{ id: number }>): void => {
    events.clear()
    for (const item of items) {
      events.set(item.id, item)
    }
  },
  subscribe: (id: number, listener: Listener): (() => void) => {
    let set = listeners.get(id)
    if (!set) {
      set = new Set()
      listeners.set(id, set)
    }
    set.add(listener)
    return () => {
      set.delete(listener)
      if (set.size === 0) listeners.delete(id)
    }
  },
}

export function useScheduleEvent<T>(id: number | undefined, fallback: T): T {
  const subscribe = useCallback(
    (listener: Listener) => (id == null ? () => undefined : scheduleEventStore.subscribe(id, listener)),
    [id],
  )
  const getSnapshot = useCallback(() => (id == null ? undefined : scheduleEventStore.get(id)), [id])
  const stored = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return (stored as T) ?? fallback
}
